import React, { useEffect, useRef, useState } from 'react';

// Helper function to get correct image URL for deployment
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // Remove leading slash if present and prepend PUBLIC_URL
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${process.env.PUBLIC_URL}/${cleanPath}`;
};

export default function ProjectModal({ project, onClose }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  useEffect(() => {
    setIndex(0);
    // Store current scroll position when modal opens
    const currentScrollY = window.scrollY;
    setSavedScrollPosition(currentScrollY);
    
    // ensure the modal mounts hidden, then become visible on next frame so the CSS transition runs
    setVisible(false);
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [project]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight' && (project?.images?.length || 0) > 1) setIndex(i => Math.min(i + 1, (project?.images?.length || 1) - 1));
      if (e.key === 'ArrowLeft' && (project?.images?.length || 0) > 1) setIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [project]);

  if (!project) return null;

  const images = project.images && project.images.length > 0 ? project.images : [];
  const next = () => setIndex(i => Math.min(i + 1, images.length - 1));
  const prev = () => setIndex(i => Math.max(i - 1, 0));
  const panelRef = useRef(null);
  // ...existing code...

  const handleClose = () => {
    setVisible(false);
    // Restore scroll position immediately, then close after animation
    window.scrollTo({
      top: savedScrollPosition,
      behavior: 'smooth'
    });
    // wait for animation
    setTimeout(() => onClose && onClose(), 300);
  };

  // while modal is open, disable nav clicks but allow scrolling
  useEffect(() => {
    const onNavPointerDown = (e) => {
      const nav = e.target && e.target.closest && e.target.closest('nav');
      if (nav) {
        // allow the nav click to proceed, but close the modal
        // schedule close on next tick so the nav handler can run
        setTimeout(() => handleClose(), 0);
      }
    };
    
    document.addEventListener('pointerdown', onNavPointerDown, true);
    
    return () => {
      document.removeEventListener('pointerdown', onNavPointerDown, true);
    };
  }, []);

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-1 sm:p-2 md:p-4 lg:p-4 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`} onClick={handleClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className={`bg-gray-900 rounded-lg md:rounded-xl shadow-2xl w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-[88vw] lg:w-[min(1200px,96%)] max-h-[82vh] overflow-hidden transform transition-all duration-300 ${visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-6 scale-95 opacity-0'} flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          <div className="md:w-1/2 bg-black flex items-center justify-center relative overflow-hidden" style={{aspectRatio: '4/3'}}>
            {images[index] ? (
              <img src={getImageUrl(images[index])} alt={`${project.title} ${index + 1}`} className="max-w-full max-h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-gray-800">
                <div className="text-6xl text-blue-400 opacity-60">🚁</div>
              </div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full">
                  <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2L2 10L10 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <button onClick={next} className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full">
                  <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 2L12 10L4 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </>
             )}
            {/* upload UI moved to admin interface */}
            {images.length > 0 && (
              <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setIndex(i)} className={`w-2 h-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/40'}`} />
                ))}
              </div>
            )}
          </div>

          {/* Progress bar - spans full modal width */}
          <div className="absolute bottom-0 left-0 h-0.5 w-full md:w-1/2">
            <div 
              className="h-full bg-blue-500 transition-all duration-150 ease-out"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>

          <div className="md:w-1/2 flex flex-col min-h-0">
            {/* Fixed header */}
            <div className="flex-shrink-0 p-3 sm:p-4 md:p-6 pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{project.title}</h3>
                  <div className="text-xs sm:text-sm text-gray-400 mt-1 flex items-center gap-2">
                    <span>
                      {project.startDate ? (
                        project.endDate ? (
                          project.endDate === 'Present' 
                            ? `${new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - Present`
                            : project.startDate === project.endDate 
                              ? new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                              : `${new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${new Date(project.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                        ) : new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      ) : (project.month ? `${project.month} ${project.year}` : project.year)}
                    </span>
                    {project.featured && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-yellow-300">
                        <path d="M12 2l2.9 6.59L22 10l-5 3.73L18 22l-6-3.82L6 22l1-8.27L2 10l7.1-1.41L12 2z" fill="currentColor"/>
                      </svg>
                    )}
                  </div>
                </div>
                <button onClick={handleClose} className="text-gray-400 hover:text-white ml-2 flex-shrink-0" aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Scrollable content area */}
            <div 
              className="flex-1 overflow-y-auto px-3 sm:px-4 md:px-6 py-3 min-h-0"
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                const maxScroll = scrollHeight - clientHeight;
                const progress = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;
                setScrollProgress(progress);
              }}
            >
              <div className="text-gray-300 leading-relaxed whitespace-pre-line text-sm sm:text-base">{project.description}</div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-4 sm:mt-6">
                {(project.tags || []).map(tag => (
                  <span key={tag} className="text-xs bg-gray-800 text-gray-200 px-3 py-1 rounded-md border border-gray-700">{tag}</span>
                ))}
              </div>
              {project.details && (
                <div className="mt-6 text-gray-300">
                  {project.details}
                </div>
              )}
            </div>

            {/* Fixed footer - ALWAYS visible */}
            <div className="flex-shrink-0 p-3 sm:p-4 md:p-6 pt-1.5">
              <div className="flex items-center mb-2 sm:mb-3">
                <h4 className="text-xs sm:text-sm text-gray-400 uppercase tracking-wide mr-3">Links</h4>
                <div className="flex-1 border-t border-gray-700" />
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {project.links && project.links.map((l, i) => (
                  <a key={i} href={l.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-black rounded-md font-semibold hover:bg-blue-400 transition text-sm sm:text-base">
                    {l.label}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="opacity-80" xmlns="http://www.w3.org/2000/svg"><path d="M14 3h7v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
